import { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { usePromptDialog } from '../hooks/usePromptDialog';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { OrgUnitFormModal } from '../features/orgUnits/OrgUnitFormModal';
import { OrgUnitTree } from '../features/orgUnits/OrgUnitTree';
import {
  createOrgUnit,
  deleteOrgUnit,
  fetchOrgUnitTree,
  reparentOrgUnit,
  updateOrgUnit,
} from '../features/orgUnits/orgUnitsApi';
import type { OrgUnitNode } from '../features/orgUnits/types';

export function OrgUnitsPage() {
  const { confirm } = useConfirmDialog();
  const { prompt } = usePromptDialog();
  const { canEdit } = usePlatformAccessLevel('org_units.access');
  const [tree, setTree] = useState<OrgUnitNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OrgUnitNode | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [parentForNew, setParentForNew] = useState<OrgUnitNode | null>(null);
  const [editing, setEditing] = useState<OrgUnitNode | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrgUnitTree();
      setTree(data);
    } catch {
      setError('No se pudo cargar la jerarquía de niveles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (payload: Parameters<typeof createOrgUnit>[0]) => {
    if (editing) {
      await updateOrgUnit(editing.id, payload);
    } else {
      await createOrgUnit(payload);
    }
    await load();
  };

  const handleDelete = async (node: OrgUnitNode) => {
    const ok = await confirm({
      title: 'Eliminar nivel',
      message: `¿Eliminar "${node.name}"?`,
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteOrgUnit(node.id);
      if (selected?.id === node.id) setSelected(null);
      await load();
    } catch {
      window.alert('No se puede eliminar: revise dependencias.');
    }
  };

  const handleReparent = async (node: OrgUnitNode) => {
    const newParentId = await prompt({
      title: 'Cambiar padre',
      message: 'ID del nuevo padre (vacío = raíz). Use con cuidado (NIV-04).',
      label: 'ID del padre',
      defaultValue: node.parentId ?? '',
      confirmText: 'Mover',
      required: false,
    });
    if (newParentId === null) return;
    try {
      await reparentOrgUnit(node.id, newParentId.trim() || null);
      await load();
    } catch {
      window.alert('No se pudo mover el nodo. Verifique jerarquía y ciclos.');
    }
  };

  return (
    <div className='mx-auto max-w-5xl p-6'>
      <div className='mb-6 flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold'>Niveles organizativos</h1>
          <p className='text-sm text-muted-foreground'>Jerarquía planta → sector → proceso → puesto (FQ-22)</p>
        </div>
        {canEdit && (
          <button
            type='button'
            className='rounded bg-blue-600 px-4 py-2 text-white'
            onClick={() => {
              setEditing(null);
              setParentForNew(null);
              setModalOpen(true);
            }}
          >
            Nuevo nodo raíz
          </button>
        )}
      </div>

      {error && <p className='mb-4 text-red-600'>{error}</p>}
      {loading ? (
        <p>Cargando…</p>
      ) : (
        <OrgUnitTree
          nodes={tree}
          canEdit={canEdit}
          selectedId={selected?.id ?? null}
          onSelect={setSelected}
          onAddChild={parent => {
            setParentForNew(parent);
            setEditing(null);
            setModalOpen(true);
          }}
          onEdit={node => {
            setEditing(node);
            setParentForNew(null);
            setModalOpen(true);
          }}
          onReparent={handleReparent}
          onDelete={handleDelete}
        />
      )}

      {selected && (
        <div className='mt-4 rounded border border-border bg-card p-4 text-sm'>
          <p>
            <strong>Seleccionado:</strong> {selected.name} ({selected.kind})
          </p>
          {selected.location && <p>Ubicación: {selected.location}</p>}
        </div>
      )}

      <OrgUnitFormModal
        open={modalOpen}
        parentNode={parentForNew}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
